/**
 * Storefront Core — the Filter Registry (frontend half).
 *
 * The registry is CODE: it says which filters exist, where each can render (Home / Shop) and what the
 * defaults are. The backend's per-domain configuration only overrides {isEnabled, displayOrder} for these
 * keys; it can never add a filter, name a component or make an unsupported filter appear in a context that
 * cannot render it. Nothing here comes from the database except two flags and an integer per known key.
 *
 * Hiding a filter removes its CONTROL only. It never changes how the URL is interpreted — an existing link
 * carrying a hidden filter's parameter still filters the results (the query is parsed and executed by the
 * server exactly as before; see ShopFiltersTemplate).
 *
 * Pure module (no React, no fetching): safe on the server and the client, and unit-tested. The keys,
 * contexts and default orders mirror MultiDomain.Domain.Constants.StorefrontFilterKeys (a drift-guard test
 * compares them).
 */

import { sanitizeFilterButtonDesign, type FilterButtonDesign } from "@/lib/theme-settings";

export type FilterContext = "home" | "shop";

export const FILTER_KEYS = [
  "productName",
  "category",
  "subcategory",
  "usageArea",
  "region",
  "color",
  "size",
  "price",
  "technicalProperties",
] as const;

export type FilterKey = (typeof FILTER_KEYS)[number];

export type FilterDefinition = {
  readonly key: FilterKey;
  readonly contexts: readonly FilterContext[];
  readonly defaultEnabled: boolean;
  /** Position in the GLOBAL default order (the Shop order, with the Home-only search box first). */
  readonly defaultOrder: number;
  /** A filter that is part of another filter's UI (Shop nests subcategories in Category): it can be switched
   * on/off but has no position of its own. */
  readonly nestedIn?: FilterKey;
};

export const FILTER_DEFINITIONS: readonly FilterDefinition[] = Object.freeze([
  { key: "productName", contexts: ["home"], defaultEnabled: true, defaultOrder: 10 },
  { key: "category", contexts: ["home", "shop"], defaultEnabled: true, defaultOrder: 20 },
  { key: "subcategory", contexts: ["shop"], defaultEnabled: true, defaultOrder: 25, nestedIn: "category" },
  { key: "usageArea", contexts: ["home", "shop"], defaultEnabled: true, defaultOrder: 30 },
  { key: "region", contexts: ["shop"], defaultEnabled: true, defaultOrder: 40 },
  { key: "color", contexts: ["home", "shop"], defaultEnabled: true, defaultOrder: 50 },
  { key: "size", contexts: ["home", "shop"], defaultEnabled: true, defaultOrder: 60 },
  { key: "price", contexts: ["home", "shop"], defaultEnabled: true, defaultOrder: 70 },
  { key: "technicalProperties", contexts: ["shop"], defaultEnabled: true, defaultOrder: 80 },
]);

/**
 * The Home panel's historical order. It differs from the global default order for Price/Size (Home has always
 * shown price before size; Shop size before price), so an UNCONFIGURED — or never-reordered — storefront keeps
 * exactly the order each page always had. Once an admin actually reorders, one order applies to both pages.
 */
export const HOME_DEFAULT_ORDER: readonly FilterKey[] = Object.freeze(["productName", "category", "usageArea", "color", "price", "size"]);

const MAX_DISPLAY_ORDER = 10000;

const DEFINITION_BY_KEY: ReadonlyMap<FilterKey, FilterDefinition> = new Map(FILTER_DEFINITIONS.map((d) => [d.key, d]));

export function isFilterKey(value: unknown): value is FilterKey {
  return typeof value === "string" && (FILTER_KEYS as readonly string[]).includes(value);
}

export function getFilterDefinition(key: FilterKey): FilterDefinition {
  return DEFINITION_BY_KEY.get(key)!;
}

export function isSupportedIn(key: FilterKey, context: FilterContext): boolean {
  return getFilterDefinition(key).contexts.includes(context);
}

/** What the backend sends inside the public domain configuration (any shape — it comes over the network). */
export type PublicFilterConfigurationItem = {
  filterKey?: unknown;
  isEnabled?: unknown;
  displayOrder?: unknown;
};

type Override = { isEnabled: boolean; displayOrder: number | null };

/**
 * Keeps only well-formed overrides for KNOWN keys. Unknown keys, wrong types and duplicates (first wins) are
 * dropped — the registry allow-list beats whatever was persisted or sent. Null when nothing valid remains
 * (= the registry defaults).
 */
export function sanitizeFilterConfiguration(raw: unknown): ReadonlyMap<FilterKey, Override> | null {
  if (!Array.isArray(raw)) return null;

  const result = new Map<FilterKey, Override>();
  for (const item of raw as PublicFilterConfigurationItem[]) {
    if (!item || typeof item !== "object") continue;
    if (!isFilterKey(item.filterKey) || result.has(item.filterKey)) continue;
    if (typeof item.isEnabled !== "boolean") continue;

    const order = item.displayOrder;
    const validOrder = typeof order === "number" && Number.isInteger(order) && order >= 0 && order <= MAX_DISPLAY_ORDER;
    result.set(item.filterKey, { isEnabled: item.isEnabled, displayOrder: validOrder ? order : null });
  }

  return result.size > 0 ? result : null;
}

type EffectiveFilter = { key: FilterKey; isEnabled: boolean; order: number };

/** Registry ∪ overrides for every key. A missing/invalid override falls back to the registry default. */
function effectiveFilters(overrides: ReadonlyMap<FilterKey, Override> | null): EffectiveFilter[] {
  return FILTER_DEFINITIONS.map((definition) => {
    const override = overrides?.get(definition.key);
    return {
      key: definition.key,
      isEnabled: override ? override.isEnabled : definition.defaultEnabled,
      order: override && override.displayOrder !== null && !definition.nestedIn ? override.displayOrder : definition.defaultOrder,
    };
  });
}

/** Deterministic order: display order, then the registry default order, then the key — never insertion order. */
function compareEffective(a: EffectiveFilter, b: EffectiveFilter): number {
  if (a.order !== b.order) return a.order - b.order;
  const byDefault = getFilterDefinition(a.key).defaultOrder - getFilterDefinition(b.key).defaultOrder;
  return byDefault !== 0 ? byDefault : a.key < b.key ? -1 : a.key > b.key ? 1 : 0;
}

const GLOBAL_DEFAULT_SEQUENCE: readonly FilterKey[] = FILTER_DEFINITIONS.filter((d) => !d.nestedIn).map((d) => d.key);

export type FilterLayout = {
  /** The standalone filters to render, in order — enabled and supported in this context only. */
  readonly keys: readonly FilterKey[];
  /** Shop only: whether the nested subcategory checkboxes render inside the Category filter. */
  readonly showSubcategories: boolean;
};

/**
 * The single source of truth for what a filter panel renders:
 *   effective domain configuration ∩ filters supported in THIS context → enabled → ordered.
 * Home, Shop desktop and Shop mobile all call this with the same configuration, so they cannot disagree.
 *
 * @param raw the public `filterConfiguration` (undefined/null/garbage → registry defaults)
 */
export function resolveFilterLayout(context: FilterContext, raw: unknown): FilterLayout {
  const overrides = sanitizeFilterConfiguration(raw);
  const effective = effectiveFilters(overrides);
  const byKey = new Map(effective.map((f) => [f.key, f]));

  const sequence = effective.filter((f) => !getFilterDefinition(f.key).nestedIn).sort(compareEffective).map((f) => f.key);
  const customized = sequence.some((key, index) => key !== GLOBAL_DEFAULT_SEQUENCE[index]);

  // Not reordered → the context's own historical order; reordered → the admin's order (restricted to the context).
  const contextOrder: readonly FilterKey[] = customized
    ? sequence.filter((key) => isSupportedIn(key, context))
    : context === "home"
      ? HOME_DEFAULT_ORDER
      : GLOBAL_DEFAULT_SEQUENCE.filter((key) => isSupportedIn(key, context));

  const keys = contextOrder.filter((key) => isSupportedIn(key, context) && byKey.get(key)!.isEnabled);
  const showSubcategories = isSupportedIn("subcategory", context) && byKey.get("subcategory")!.isEnabled && keys.includes("category");

  return { keys, showSubcategories };
}

/**
 * The layout WITHOUT the price-range filter. The price filter prints the catalog's real min/max prices as placeholders and filters
 * on them, so when the domain hides prices the control must not exist. Like every filter switch this hides the CONTROL only: the
 * `minPrice`/`maxPrice` query parameters keep their meaning (no query semantics change).
 */
export function withoutPriceFilter(layout: FilterLayout): FilterLayout {
  return layout.keys.includes("price") ? { ...layout, keys: layout.keys.filter((key) => key !== "price") } : layout;
}

// ---------------------------------------------------------------------------------------------
// Primary filter action button (THEME styling, from the domain's active-theme settings)
// ---------------------------------------------------------------------------------------------

/**
 * The className/style of the PRIMARY filter action button (Home "Show Products", Shop "Apply" — desktop and the
 * mobile sheet). With no override it returns the caller's original className and NO style, so the markup is
 * exactly what it always was. With an override only the validated colors become inline styles, plus one fixed
 * class that keeps a visible hover (the theme's `hover:bg-primary-dark` cannot beat an inline background).
 * Reset / Clear / Advanced-filters links, the mobile "Filters" toggle, checkboxes and ranges are never passed here.
 */
export function filterButtonAttributes(
  design: FilterButtonDesign | null | undefined,
  baseClassName: string,
): { className: string; style?: { backgroundColor?: string; color?: string } } {
  const safe = sanitizeFilterButtonDesign(design);
  if (!safe.backgroundColor && !safe.textColor) return { className: baseClassName };

  return {
    className: `${baseClassName} filter-cta-custom`,
    style: {
      ...(safe.backgroundColor ? { backgroundColor: safe.backgroundColor } : {}),
      ...(safe.textColor ? { color: safe.textColor } : {}),
    },
  };
}
